import {
  Alert,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import classNames from "classnames";
import * as React from "react";

import { useCurrentOrgId } from "@/providers/auth/hooks";
import { RobotoDivider } from "@/shared/components/RobotoDivider";
import { RobotoTypography } from "@/shared/components/RobotoTypography";
import { IngestAsAssetDialog } from "@/shared/components/visualization/IngestAsAssetDialog";
import { type FileRecord } from "@/shared/domain/files";
import { useOrgFileSearch } from "@/shared/domain/files/hooks";
import {
  ingestableAssetFileTypes,
  readVisualizationAsset,
} from "@/shared/domain/urdf";
import { useResolveVisualizationAssetFile } from "@/shared/domain/urdf/hooks";
import { Button, TextInput } from "@/shared/foundation/primitives";
import { useDebouncedCallback } from "@/shared/hooks";
import { ErrorMonitoringService } from "@/shared/services/ErrorMonitoringService";
import type { File as ThreeDFile } from "@/shared/state/visualization/schema/v1/file";

import styles from "./ModelFilePicker.module.css";
import { modelReadiness } from "./modelReadiness";
import { useDatasetLabels } from "./useDatasetLabels";

/**
 * A chosen model, and the file the user actually chose.
 *
 * The two always differ: the user selects `robot.urdf`, and what the panel must
 * reference is the glTF the platform generated from it — a file under
 * `.VISUALIZATION_ASSETS/` with a derived name nobody has seen. `model` is what
 * gets loaded; `source` is what gets shown, and is the only handle by which a
 * later re-ingest could be noticed.
 *
 * Both fields are therefore required. That is narrower than the mesh schema,
 * where `source` stays optional: a workspace saved when the picker still
 * offered a `.gltf` directly holds a model with no source, and must keep
 * loading.
 */
export interface PickedModel {
  /** The glTF the mesh will reference — the artifact, never the description. */
  model: ThreeDFile;
  /** The robot description or point cloud scan it was generated from. */
  source: ThreeDFile;
}

interface ModelFilePickerProps {
  /** The entity the chosen model will be imported beneath, named in the title. */
  entityId: string;
  onClose: () => void;
  /**
   * Called with the resolved model. The picker keeps itself open until this
   * fires, so the caller is what dismisses it.
   */
  onSubmit: (picked: PickedModel) => void;
  /** Called when the pick could not be resolved to a loadable model. */
  onError: (message: string) => void;
  /**
   * Whether picking an unconverted file may start a conversion.
   *
   * False for a caller without `release-3d-assets`: the list still opens, and
   * already-converted models are still pickable, but nothing here can launch a
   * worker run. See {@link modelReadiness}.
   */
  canIngest: boolean;
}

/** How long typing settles before a new search is issued. */
const SEARCH_DEBOUNCE_MS = 300;

/** The reference the panel stores, from the record the search returned. */
function toThreeDFile(record: FileRecord): ThreeDFile {
  return {
    fileId: record.file_id,
    relativePath: record.relative_path,
    // Carried so a later staleness check knows which dataset to look in, and so
    // a cross-org load has a dataset to scope itself to.
    associationId: record.association_id,
  };
}

/**
 * One searchable list of every robot description in the org.
 *
 * Mounted only while open, so that the org context it needs is resolved only
 * when someone actually picks a file. Calling those hooks from `MeshConfig`
 * itself would make every entity's mesh form — a plain set of shape controls —
 * depend on being inside an authenticated provider tree.
 *
 * ## Why one flat list, and not a dataset browser
 *
 * This used to compose the shared `FilePicker`, which is a two-pane browse:
 * pick a dataset on the left, then a file on the right. That shape asks the
 * user to already know which dataset holds their robot, which is the one thing
 * they are here to find out. `FilePicker` is still exactly right for its other
 * consumers — adding several files from a dataset the user *is* working in —
 * so it is untouched, and this list is built locally rather than by bending a
 * shared component around a single caller.
 *
 * ## What a row has to say
 *
 * Dropping the dataset panel dropped the disambiguation it was quietly doing:
 * two datasets can each hold a `robot.urdf`, and stacked in one list those rows
 * are identical. Every row therefore carries its own location — the dataset it
 * lives in, and its dataset-relative path — because either alone is
 * insufficient. Two files can share a path across datasets, and two files can
 * share a dataset while differing only by directory. The search term is matched
 * against the path for the same reason: someone holding
 * `Customer URDF/lemi2p/robot.urdf` and `Customer URDF/other/robot.urdf` can
 * only separate them by typing part of the directory.
 *
 * ## Why robot descriptions are the only thing listed
 *
 * The generated glTFs are unreachable from here — they live under
 * `.VISUALIZATION_ASSETS/`, and every file query drops paths beginning with a
 * dot before any other condition is applied. Listing the `.urdf` instead is not
 * a workaround for that: the URDF is the artifact of record — what the user
 * uploaded and named, what carries the conversion's status, what a re-upload
 * changes — while the glTF is a derived cache keyed on its upload.
 *
 * A hand-authored `.gltf`/`.glb` sitting in the same dataset is deliberately
 * *not* offered, even though the panel could load it. Such a file has no ingest
 * behind it, so nothing states whether it is current, nothing regenerates it
 * when its source moves, and the joints the settings bind against are whatever
 * happened to be exported. Every model added here is therefore backed by an
 * ingested asset, and every one of them resolves to its artifact on submit:
 * downstream keeps receiving a glTF reference and never learns that robot
 * descriptions exist.
 */
export function ModelFilePicker({
  entityId,
  onClose,
  onSubmit,
  onError,
  canIngest,
}: ModelFilePickerProps) {
  const orgId = useCurrentOrgId();
  const resolveAssetFile = useResolveVisualizationAssetFile();

  /** What is in the box, updated on every keystroke. */
  const [typed, setTyped] = React.useState("");
  /** What has actually been searched for — `typed`, once it settles. */
  const [searchTerm, setSearchTerm] = React.useState("");
  /** The row whose artifact is being looked up, while that is in flight. */
  const [resolving, setResolving] = React.useState<string | undefined>();
  /**
   * The file the ingest wizard is open on, if any.
   *
   * A row with no artifact behind it is not a dead end here. The wizard is the
   * one way an ingestable source becomes a model, and the picker is one of the
   * places a user discovers they need it, so it is raised over the list rather
   * than sending them back to the dataset's file browser to find the same file
   * again.
   */
  const [ingesting, setIngesting] = React.useState<FileRecord | null>(null);
  /**
   * Set when the dialog is dismissed, so a resolution that lands afterwards is
   * dropped instead of reporting a pick the user has already walked away from.
   */
  const dismissedRef = React.useRef(false);

  const applySearchTerm = useDebouncedCallback(
    setSearchTerm,
    SEARCH_DEBOUNCE_MS,
  );

  const filesQuery = useOrgFileSearch({
    // The search route answers a missing org with an empty page rather than an
    // error, so this is the difference between "no robots" and a silent
    // scoping bug. It is also the *browsing* org: the org that owns any given
    // result is read back off that record, never assumed to be this one.
    orgId,
    searchTerm,
    // Ingestable sources only — robot descriptions and point cloud scans. A
    // `.gltf`/`.glb` in the same dataset is a model the panel could load, but
    // not one the platform ingested, so it is not on offer — see the note
    // above. Filtering happens in the query rather than over the response, so
    // an excluded file is never even paged in.
    extensions: ingestableAssetFileTypes,
  });

  const records = React.useMemo(() => filesQuery.data ?? [], [filesQuery.data]);

  const datasetIds = React.useMemo(
    () => records.map((record) => record.association_id),
    [records],
  );
  const datasetLabel = useDatasetLabels(orgId, datasetIds);

  const handleClose = React.useCallback(() => {
    dismissedRef.current = true;
    onClose();
  }, [onClose]);

  /**
   * Turns a chosen source file into the model the mesh will reference.
   *
   * @param record the robot description or point cloud scan the user chose
   * @param assetRelativePath where the artifact is, when the caller knows
   *   better than the record does — which the wizard does, having just written
   *   it. The record in hand came from a search issued before the conversion
   *   existed, so its status block still says nothing about the artifact.
   */
  const handlePick = React.useCallback(
    (record: FileRecord, assetRelativePath?: string) => {
      setResolving(record.file_id);

      // Every listed row is an ingestable source, so every pick is a lookup:
      // what the mesh references is the artifact, not the file just clicked.
      // The lookup is scoped to the record's own org, which is not necessarily
      // the org being browsed — a shared dataset is listed under one and owned
      // by another.
      void resolveAssetFile(
        record,
        assetRelativePath ?? readVisualizationAsset(record)?.relative_path,
      )
        .then((artifact) => {
          if (dismissedRef.current) {
            return;
          }
          if (artifact === null) {
            // The row said `completed`, so this is the platform's artifact
            // having gone missing rather than anything the user did wrong.
            setResolving(undefined);
            onError(
              `The 3D model generated from ${record.name} could not be found. Try converting it again.`,
            );
            return;
          }
          onSubmit({
            model: toThreeDFile(artifact),
            source: toThreeDFile(record),
          });
        })
        .catch((error: unknown) => {
          // Deliberately not the underlying message: a transport error names
          // the artifact's path, which is the one string this flow exists to
          // keep off the screen. The detail goes to monitoring instead.
          ErrorMonitoringService.captureError(
            error instanceof Error ? error : new Error(String(error)),
          );
          if (dismissedRef.current) {
            return;
          }
          setResolving(undefined);
          onError(`Could not load the 3D model generated from ${record.name}`);
        });
    },
    [onError, onSubmit, resolveAssetFile],
  );

  /**
   * Sends a row with no artifact behind it through the ingest wizard.
   *
   * The pick is not abandoned by doing so — see {@link handleIngested}. The
   * picker stays mounted underneath, so leaving the wizard leaves the user on
   * the list they were choosing from.
   */
  const handleIngest = React.useCallback((record: FileRecord) => {
    setIngesting(record);
  }, []);

  /**
   * Finishes the pick the conversion was started for.
   *
   * Converting from here is not a detour: the user came to choose a model, and
   * the wizard they were sent through is what produced it. Asking them to find
   * the same row a second time would be asking them to repeat a choice they
   * have already made.
   *
   * The path comes from the wizard rather than from the record, which was
   * listed by a search issued before the artifact existed and whose status
   * block therefore still says nothing about it.
   */
  const handleIngested = React.useCallback(
    (source: FileRecord, assetRelativePath?: string) => {
      handlePick(source, assetRelativePath);
    },
    [handlePick],
  );

  return (
    <>
      <Dialog
        open
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ className: styles.dialogPaper }}
      >
        <DialogTitle className={styles.title}>
          <h1 className={styles.titleText}>Choose a model for {entityId}</h1>
          <RobotoTypography className={styles.subtitleText}>
            Robot descriptions (.urdf) and point clouds (.ply) across every
            dataset in this organization.
          </RobotoTypography>
        </DialogTitle>

        <div className={styles.searchRow}>
          <TextInput
            autoFocus
            type="search"
            aria-label="Search robot descriptions"
            placeholder="Search by file name or path…"
            value={typed}
            onChange={(event) => {
              setTyped(event.target.value);
              applySearchTerm(event.target.value);
            }}
          />
        </div>

        <RobotoDivider />

        <DialogContent className={styles.content}>
          <PickerBody
            canIngest={canIngest}
            error={filesQuery.error}
            loading={filesQuery.isPending}
            records={records}
            searchTerm={searchTerm}
            // Between a keystroke and the search it triggers, whatever is on
            // screen answers a question the user has already moved off. That is
            // only visible when there is nothing to show — a stale *list* is
            // being narrowed in front of them, but a stale "nothing matched"
            // reads as a definite answer about the term they are still typing.
            settled={typed === searchTerm}
            resolving={resolving}
            datasetLabel={datasetLabel}
            onPick={handlePick}
            onIngest={handleIngest}
          />

          {filesQuery.hasNextPage && (
            <Button
              className={styles.loadMore}
              onClick={() => void filesQuery.fetchNextPage()}
              disabled={filesQuery.isFetchingNextPage}
              loading={filesQuery.isFetchingNextPage}
            >
              Load more
            </Button>
          )}
        </DialogContent>

        <RobotoDivider />

        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
        </DialogActions>
      </Dialog>
      {/* Mounted after the list, so it stacks over it: two plain MUI dialogs
          order by mount rather than by any z-index of their own. */}
      <IngestAsAssetDialog
        file={ingesting}
        onClose={() => setIngesting(null)}
        onIngested={handleIngested}
      />
    </>
  );
}

interface PickerBodyProps {
  error: Error | null;
  loading: boolean;
  records: FileRecord[];
  searchTerm: string;
  settled: boolean;
  resolving: string | undefined;
  datasetLabel: (datasetId: string) => string;
  onPick: (record: FileRecord) => void;
  onIngest: (record: FileRecord) => void;
  /** Whether an unconverted row may be picked to start a conversion. */
  canIngest: boolean;
}

/**
 * The list, or the reason there isn't one.
 *
 * The four states below are four different things to tell a user, and
 * collapsing any of them into a blank box has been a bug in this feature more
 * than once. In particular "still asking" is never rendered as an answer:
 * an empty list only becomes "nothing matched" once a settled query has said so.
 */
function PickerBody({
  error,
  loading,
  records,
  searchTerm,
  settled,
  resolving,
  datasetLabel,
  onPick,
  onIngest,
  canIngest,
}: PickerBodyProps) {
  if (error !== null) {
    return (
      <Alert severity="error" className={styles.status}>
        Could not search for robot descriptions. Try closing and reopening this
        dialog.
      </Alert>
    );
  }

  if (loading || (records.length === 0 && !settled)) {
    return (
      <div className={styles.status}>
        <CircularProgress size={16} />
        <RobotoTypography variant="body2">Searching…</RobotoTypography>
      </div>
    );
  }

  if (records.length === 0) {
    return (
      <div className={styles.status}>
        <RobotoTypography variant="body2" color="text.secondary">
          {searchTerm === ""
            ? "No robot descriptions or point clouds in this organization. Upload a .urdf or .ply to a dataset to make it available here."
            : `No robot descriptions or point clouds match “${searchTerm}”.`}
        </RobotoTypography>
      </div>
    );
  }

  return (
    <ul className={styles.list}>
      {records.map((record) => (
        <ModelFileRow
          key={record.file_id}
          record={record}
          datasetLabel={datasetLabel(record.association_id)}
          resolving={resolving === record.file_id}
          // While one row is being resolved no other row may be chosen: a
          // second pick would race the first, and whichever lookup returned
          // last would win regardless of which the user asked for second.
          busy={resolving !== undefined}
          canIngest={canIngest}
          onPick={onPick}
          onIngest={onIngest}
        />
      ))}
    </ul>
  );
}

interface ModelFileRowProps {
  record: FileRecord;
  datasetLabel: string;
  resolving: boolean;
  busy: boolean;
  onPick: (record: FileRecord) => void;
  onIngest: (record: FileRecord) => void;
  /** Whether an unconverted row may be picked to start a conversion. */
  canIngest: boolean;
}

/**
 * One ingestable source, and everything needed to choose between it and a
 * same-named one three datasets away.
 *
 * A file with no current artifact stays on the list, saying so — rather than
 * being filtered out. A user who knows `broken.urdf` is in the dataset and
 * cannot find it here has been told the picker is broken; one who finds it
 * reading "Conversion failed" has been told something true, and is offered the
 * conversion that fixes it.
 *
 * Only a run already in flight is unpickable: nothing the user can press makes
 * a running worker finish sooner, and there is no artifact yet to load.
 */
function ModelFileRow({
  record,
  datasetLabel,
  resolving,
  busy,
  onPick,
  onIngest,
  canIngest,
}: ModelFileRowProps) {
  const { pick, reason } = modelReadiness(record, canIngest);
  const status = resolving ? "Loading model…" : reason;

  return (
    <li>
      <button
        type="button"
        className={classNames(styles.row, {
          [styles.rowUnavailable]: pick === null,
        })}
        disabled={pick === null || busy}
        onClick={() => (pick === "ingest" ? onIngest(record) : onPick(record))}
      >
        <span className={styles.rowText}>
          <span className={styles.rowName}>{record.name}</span>
          <span className={styles.rowLocation}>
            {datasetLabel} · {record.relative_path}
          </span>
        </span>
        {status !== undefined && (
          <span className={styles.rowStatus}>{status}</span>
        )}
      </button>
    </li>
  );
}
